import { BookOpen, ListOrdered, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ListCard } from './types';
import { pluralFollowers, pluralWorks } from './listCardUtils';

const cardShell =
    'group relative flex w-full flex-col overflow-hidden rounded-xl border border-white/10 bg-white/[0.02] text-left text-inherit no-underline transition-all duration-200 ease-out motion-reduce:transition-none motion-reduce:hover:translate-y-0 hover:-translate-y-0.5 hover:border-orange-500/25 hover:shadow-[0_8px_30px_-10px_rgba(249,115,22,0.35)] motion-reduce:hover:shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40';

const imgMotion =
    'h-full w-full object-cover motion-safe:transition-transform motion-safe:duration-500 motion-safe:group-hover:scale-[1.05] motion-reduce:group-hover:scale-100';

export type ListCardMosaicProps =
    | { list: ListCard; onOpen: () => void; to?: undefined }
    | { list: ListCard; to: string; onOpen?: undefined };

function MosaicCovers({ covers, worksCount }: { covers: string[]; worksCount: number }) {
    const n = covers.length;

    if (n === 0) {
        return (
            <div className="flex aspect-[1/1] w-full items-center justify-center bg-white/[0.03] md:aspect-[3/4]">
                <ListOrdered className="h-12 w-12 text-white/15 md:h-14 md:w-14" aria-hidden />
            </div>
        );
    }

    if (n === 1) {
        const moreSingle = worksCount > 1 ? worksCount - 1 : 0;
        return (
            <div className="relative aspect-[1/1] w-full overflow-hidden bg-white/5 p-0.5 md:aspect-[3/4]">
                <div className="relative h-full w-full overflow-hidden">
                    <img src={covers[0]} alt="" className={imgMotion} />
                    {moreSingle > 0 ? (
                        <div
                            className="absolute inset-0 flex items-center justify-center bg-black/65 text-lg font-bold tabular-nums text-white md:text-xl"
                            aria-hidden
                        >
                            +{moreSingle}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }

    const slots: (string | null)[] = [0, 1, 2, 3].map((i) => covers[i] ?? null);
    const thumbsShown = Math.min(4, covers.length);
    const moreCountGrid = worksCount > thumbsShown ? worksCount - thumbsShown : 0;
    const overlayIdx = moreCountGrid > 0 ? thumbsShown - 1 : -1;

    return (
        <div className="relative aspect-[1/1] w-full bg-white/5 p-0.5 md:aspect-[3/4]">
            <div className="grid h-full min-h-0 grid-cols-2 grid-rows-2 gap-0.5">
                {slots.map((src, i) => {
                    const showMore = moreCountGrid > 0 && i === overlayIdx && Boolean(src);
                    return (
                        <div key={i} className="relative min-h-0 overflow-hidden bg-white/[0.03]">
                            {src ? (
                                <>
                                    <img src={src} alt="" className={imgMotion} />
                                    {showMore ? (
                                        <div
                                            className="absolute inset-0 flex items-center justify-center bg-black/65 text-base font-bold tabular-nums text-white md:text-xl"
                                            aria-hidden
                                        >
                                            +{moreCountGrid}
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function ListCardMosaic(props: ListCardMosaicProps) {
    const { list } = props;
    const { name, description, isPrivate, worksCount, followers, covers } = list;
    const descText =
        worksCount === 0 ? 'Sem obras ainda' : description?.trim() || 'Sem descrição';

    const aria = `Abrir lista ${name}, ${pluralWorks(worksCount)}`;

    const inner = (
        <>
            <div className="relative w-full shrink-0">
                <MosaicCovers covers={covers} worksCount={worksCount} />
            </div>

            <div className="flex flex-col space-y-1.5 p-2.5 md:p-4">
                <span className="truncate text-sm font-semibold tracking-tight text-white md:text-base" title={name}>
                    {name}
                </span>
                <p className="line-clamp-2 min-h-[2.25em] text-xs text-white/55 md:min-h-[2.5em] md:text-[13px]">{descText}</p>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-white/50 md:text-xs">
                    <span className="inline-flex items-center gap-1">
                        <BookOpen className="h-3 w-3 shrink-0" aria-hidden />
                        {pluralWorks(worksCount)}
                    </span>
                    {!isPrivate ? (
                        <>
                            <span className="h-1 w-1 shrink-0 rounded-full bg-white/20" aria-hidden />
                            <span className="inline-flex items-center gap-1">
                                <Users className="h-3 w-3 shrink-0" aria-hidden />
                                {pluralFollowers(followers)}
                            </span>
                        </>
                    ) : null}
                </div>
            </div>
        </>
    );

    if ('to' in props && props.to) {
        return (
            <Link to={props.to} className={cardShell} aria-label={aria}>
                {inner}
            </Link>
        );
    }

    return (
        <button type="button" onClick={props.onOpen} className={cardShell} aria-label={aria}>
            {inner}
        </button>
    );
}
